/** 儲存空間資訊與持久化申請（spec 19.1）。 */

export interface StorageEstimateResult {
  usage: number | null;
  quota: number | null;
  supported: boolean;
  /** Chromium 的 usageDetails（indexedDB、caches…）；其他瀏覽器沒有，就是 null。 */
  details: Record<string, number> | null;
}

const UNSUPPORTED: StorageEstimateResult = {
  usage: null,
  quota: null,
  supported: false,
  details: null,
};

export async function estimateStorage(): Promise<StorageEstimateResult> {
  const manager = navigator.storage as StorageManager | undefined;
  if (!manager || typeof manager.estimate !== 'function') return UNSUPPORTED;

  try {
    const estimate = await manager.estimate();
    // usageDetails 是 Chromium 的非標準欄位，有就順便顯示，沒有也不影響主要數字。
    const details = (estimate as { usageDetails?: Record<string, number> }).usageDetails ?? null;
    return {
      usage: estimate.usage ?? null,
      quota: estimate.quota ?? null,
      supported: true,
      details: details && Object.keys(details).length > 0 ? details : null,
    };
  } catch {
    return UNSUPPORTED;
  }
}

/** 接近配額上限時提高備份提醒層級（spec 20.7）。 */
const NEAR_LIMIT_RATIO = 0.8;

export interface DescribedStorage {
  usedLabel: string;
  quotaLabel: string;
  ratio: number | null;
  /** 「0.6%」這種好讀的比例；無法取得時是 null。 */
  percentLabel: string | null;
  nearLimit: boolean;
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${value} ${units[unit]}` : `${value.toFixed(1)} ${units[unit]}`;
}

export function describeStorageEstimate(estimate: StorageEstimateResult): DescribedStorage {
  if (!estimate.supported || estimate.usage === null || estimate.quota === null) {
    return {
      usedLabel: '無法取得',
      quotaLabel: '無法取得',
      ratio: null,
      percentLabel: null,
      nearLimit: false,
    };
  }

  const ratio = estimate.quota > 0 ? estimate.usage / estimate.quota : 0;
  return {
    usedLabel: formatBytes(estimate.usage),
    quotaLabel: formatBytes(estimate.quota),
    ratio,
    // 用量通常遠小於配額，四捨五入到整數會一路顯示 0%，所以小數點留一位。
    percentLabel: `${(ratio * 100).toFixed(ratio > 0 && ratio < 0.001 ? 3 : 1)}%`,
    nearLimit: ratio >= NEAR_LIMIT_RATIO,
  };
}

/**
 * 申請持久化儲存，降低瀏覽器自動清除站台資料的風險。
 * 被拒絕時**不阻斷使用**，只是備份提醒會更積極。
 */
export async function requestPersistentStorage(): Promise<boolean> {
  const manager = navigator.storage as StorageManager | undefined;
  if (!manager || typeof manager.persist !== 'function') return false;

  try {
    if (typeof manager.persisted === 'function' && (await manager.persisted())) return true;
    return await manager.persist();
  } catch {
    return false;
  }
}
